import {DomainObject} from 'shared/store';
import { observable, computed, action, makeObservable } from 'mobx';
import _ from 'lodash';
import FeaturePermission from 'main/stores/users/FeaturePermission';
import DataPermission from 'main/stores/users/DataPermission';
import dataPermissions from 'main/stores/users/dataPermissions';
import {t} from 'shared/core';

class Role extends DomainObject {
  id;
  name;
  featurePermissions = [];
  employeeDataPermissions = [];
  users = [];
  locations = [];
  departments = [];
  employmentTypes = [];
  scope;
  roleType;

  _relations = {
    featurePermissions: [FeaturePermission],
    employeeDataPermissions: [DataPermission]
  };

  constructor(other) {
    super();

    makeObservable(this, {
      id: observable,
      name: observable,
      featurePermissions: observable,
      employeeDataPermissions: observable,
      users: observable,
      locations: observable,
      departments: observable,
      employmentTypes: observable,
      scope: observable,
      roleType: observable,
      nameView: computed,
      isCustom: computed,
      isAdmin: computed,
      isManager: computed,
      employeeCount: computed,
      addUser: action,
      removeUser: action,
      addLocation: action,
      removeLocation: action,
      handleEmploymentTypeChange: action,
      addEmploymentType: action,
      removeEmploymentType: action,
      addDepartment: action,
      removeDepartment: action,
      updateDataPermissions: action
    });

    this.merge(other);
  }

  _afterMerge() {
    this.updateDataPermissions();
  }

  get nameView() {
    switch (this.roleType) {
      case 'admin':
        return t('company_settings.permissions.default_roles.Super Admin');
      case 'manager':
        return t('company_settings.permissions.default_roles.Manager');
      case 'employee':
        return t('company_settings.permissions.default_roles.Employee');
      case 'custom':
      default:
        return this.name;
    }
  }

  get isCustom() {
    return this.roleType === 'custom';
  }

  get isAdmin() {
    return this.roleType === 'admin';
  }

  get isManager() {
    return this.roleType === 'manager';
  }

  get employeeCount() {
    return this.users.length;
  }

  addUser(user) {
    const existingUser = _.find(this.users, {id: user.id});
    if (!existingUser) {
      this.users.push(user);
    }
  }

  removeUser(user) {
    this.users.remove(_.find(this.users, {id: user.id}));
  }

  addLocation(location) {
    const existingLocation = _.find(this.locations, {id: location.id});
    if (!existingLocation) {
      this.locations.push(location);
    }
  }

  removeLocation(location) {
    this.locations.remove(_.find(this.locations, {id: location.id}));
  }

  handleEmploymentTypeChange(change) {
    if (change.action === 'add') {
      this.addEmploymentType(change.value);
    } else if (change.action === 'remove') {
      this.removeEmploymentType(change.value);
    }
  }

  addEmploymentType(employmentType) {
    const existingEmploymentType = this.employmentTypes.includes(employmentType);
    if (!existingEmploymentType) {
      this.employmentTypes.push(employmentType);
    }
  }

  removeEmploymentType(employmentType) {
    this.employmentTypes.remove(employmentType);
  }

  addDepartment(department) {
    const existingDepartment = _.find(this.departments, {id: department.id});
    if (!existingDepartment) {
      this.departments.push(department);
    }
  }

  removeDepartment(department) {
    this.departments.remove(_.find(this.departments, {id: department.id}));
  }

  updateDataPermissions() {
    const givenDataPermissions = this.employeeDataPermissions;

    this.employeeDataPermissions = dataPermissions;
    for (const p of this.employeeDataPermissions) {
      const existing = _.find(givenDataPermissions, {name: p.name});

      if (existing) {
        p.merge(existing);
      }
    }
  }
}

export default Role;
